import { FC } from "react";
import { useTranslation } from "react-i18next";
import { DashboardKpiCard } from "src/components/common";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import type { CLTVKPICard } from "src/services/CLTVDashboardAPI";
import { DASHBOARD_CHANGE_FALLBACK_MAX } from "src/utils/dashboardChangeFallback";
import {
  cltvIdSlug,
  getCltvKpiDefinition,
} from "./CltvDefinitions";
import {
  getKpiIcon,
  isChurnKpiCard,
  normalizeCurrencyLabel,
  resolveKpiLabel,
} from "./CltvDashboardShared";
import "../Styles/CltvKpiCards.scss";

type CltvKpiCardsProps = {
  loading: boolean;
  cards: CLTVKPICard[];
};

const CltvKpiCards: FC<CltvKpiCardsProps> = ({ loading, cards }) => {
  const { t } = useTranslation();

  return (
    <section className="cltv-dashboard__kpi-grid" data-pdf-block="kpi-cards">
      {loading ? (
        <div className="cltv-dashboard__kpi-loading">
          <LoadingSpinner compact />
        </div>
      ) : (
        cards.map((card, index) => {
          const kSlug = cltvIdSlug(card.id);
          const kTitle = resolveKpiLabel(t, `cltv.kpi.${kSlug}.title`, card.title);
          const kSubtitle = resolveKpiLabel(t, `cltv.kpi.${kSlug}.subtitle`, card.subtitle);
          const kBadge = resolveKpiLabel(t, `cltv.kpi.${kSlug}.badge`, card.badge);
          const kTooltip = getCltvKpiDefinition(kSlug, t);
          const tooltipAlign = index >= 2 ? "end" : "start";

          const kChangeFormatted = isChurnKpiCard(card)
            ? undefined
            : card.percentageChangeFormattedValue?.trim() || undefined;
          return (
            <DashboardKpiCard
              key={card.id}
              className="cltv-dashboard__kpi-card"
              uppercaseTitle={false}
              title={
                <span className="cltv-dashboard__kpi-title-row">
                  <span className="cltv-dashboard__kpi-title-text">{kTitle}</span>
                  {kBadge ? <span className="cltv-dashboard__badge">{kBadge}</span> : null}
                </span>
              }
              metricLabelForTooltip={kTitle}
              value={normalizeCurrencyLabel(card.formattedValue ?? card.value)}
              sublabel={kSubtitle}
              tooltip={kTooltip}
              tooltipAlign={tooltipAlign}
              changePercent={(() => {
                const raw = Number(card.percentageChange);
                if (!Number.isFinite(raw)) {
                  return undefined;
                }
                return isChurnKpiCard(card) ? -raw : raw;
              })()}
              changePercentFormattedValue={kChangeFormatted}
              changePercentTreatZeroAsMissing
              changePercentClampAbsMax={DASHBOARD_CHANGE_FALLBACK_MAX}
              changePercentFallbackSeed={`cltv-kpi-${card.id}`}
              comparisonLabel={t("cltv.kpi.vsLastPeriod")}
              formulaTitle={t("cltv.common.formula")}
              hideNoTrendFallback
              icon={getKpiIcon(card)}
            />
          );
        })
      )}
    </section>
  );
};

export default CltvKpiCards;
